import { AxiomReportType } from '@/enums/axiom-report.enum'
import { ActionMode } from '@/enums/scenario-builder.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { TReportTemplate } from '@/interfaces/scenarioBuilder.interface'
import {
  setActionMode,
  setSelectedReportTemplate,
  toggleCreateReportModalOpened,
} from '@/states/scenarioBuilder.slice'
import { showSnackbar } from '@/states/snackbar.slice'
import { validateTemplateName } from '@/utils/UserInputValidation'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import ReportTypeDropdown from '../ReportType/ReportType'
import LoadingButton from '../Shared/LoadingButton'

const emptyTemplate: Partial<TReportTemplate> = {
  name: '',
  reportType: AxiomReportType.SCEN,
  lastSavedTime: '',
}

export const CreateReportModal = () => {
  const dispatch = useDispatch()
  const [loading] = useState(false)
  const [newTemplate, setNewTemplate] = useState<
    Partial<TReportTemplate> | undefined
  >(emptyTemplate)

  const isCreateReportModalOpened = useAppSelector(
    (s) => s.scenarioBuilder.isCreateReportModalOpened,
  )
  //methods
  const handleClose = () => {
    dispatch(toggleCreateReportModalOpened(false))
  }

  const handleSubmit = () => {
    const templateName = newTemplate?.name || ''
    
    // Validate template name
    const validation = validateTemplateName(templateName)
    if (!validation.isValid) {
      dispatch(
        showSnackbar({
          message: validation.error || 'Invalid template name',
          severity: 'error',
        }),
      )
      return
    }

    const templateWithName = {
      ...newTemplate,
      templateName: validation.sanitized || templateName,
    };

    dispatch(setActionMode(ActionMode.CREATE))
    dispatch(
      setSelectedReportTemplate(templateWithName as unknown as TReportTemplate),
    )
    dispatch(toggleCreateReportModalOpened(false))
  }

  const isFormEmpty = () => {
    return newTemplate && newTemplate?.name === ''
  }

  return (
    <Dialog
      fullWidth
      maxWidth='xs'
      open={isCreateReportModalOpened}
      onClose={handleClose}
      aria-labelledby='max-width-dialog-title'
      data-cy='create-report-modal'
    >
      <DialogTitle>
        <Typography variant='h5'>Create New Template</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <TextField
          size='small'
          label='Template Name'
          data-cy='scenario-name-input'
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNewTemplate((prevState) => ({
              ...prevState,
              name: e.target.value,
            }))
          }
          required
          variant='outlined'
          type='text'
          fullWidth
          value={newTemplate?.name}
        />
        <ReportTypeDropdown 
          value={newTemplate?.reportType}
          onChange={(reportType) => 
            setNewTemplate((prevState) => ({
              ...prevState,
              reportType,
            }))
          }
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <LoadingButton
          loading={loading}
          onClick={handleSubmit}
          disabled={isFormEmpty()}
          data-cy='create-template-button'
        >
          Create
        </LoadingButton>
        <Button onClick={handleClose} data-cy='cancel-button'>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
